import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { RefreshCw, Info } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import Money from "@/components/Money";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { ChartSkeleton, CardSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";

export default function Salary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { format, convert, currency } = useCurrency();

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/salary"); setData(r.data.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try { const r = await api.post("/salary/refresh"); setData(r.data.data); toast.success("Salary refreshed"); }
    catch { toast.error("Refresh failed"); } finally { setRefreshing(false); }
  };

  if (loading) return (
    <div>
      <PageHeader eyebrow="Module 02" title="Salary Intelligence" subtitle="Loading market analysis…" />
      <div className="grid md:grid-cols-4 gap-3"><CardSkeleton /><CardSkeleton /><CardSkeleton /><CardSkeleton /></div>
      <div className="mt-6 grid lg:grid-cols-2 gap-4"><ChartSkeleton /><ChartSkeleton /></div>
    </div>
  );
  if (!data) return (
    <div>
      <PageHeader eyebrow="Module 02" title="Salary Intelligence" subtitle="Add your current role and salary, then generate the market analysis." />
      <EmptyState
        icon={RefreshCw} title="No salary report yet"
        description="Complete your profile (role, salary, country). Then click Generate — AI will fetch local labor-market data in your currency."
        actionLabel="Generate report" onAction={refresh} testId="salary-generate-empty"
      />
    </div>
  );

  // AI now returns data.currency for the source currency (defaults USD)
  const from = (data.currency || "USD").toUpperCase();
  const converted = from !== currency;

  const barData = [
    { name: "Current", salary: convert(data.current_salary, from) || 0 },
    { name: "Market median", salary: convert(data.market_median, from) || 0 },
    { name: "+ Certification", salary: convert(data.potential_with_certification, from) || 0 },
    { name: "+ Promotion", salary: convert(data.potential_with_promotion, from) || 0 },
    { name: "+ Industry switch", salary: convert(data.potential_industry_switch, from) || 0 },
    { name: "Top 10%", salary: convert(data.market_top_10, from) || 0 },
  ];
  const progression = (data.salary_by_experience || []).map((r) => ({
    ...r, salary: convert(r.salary, from) || 0,
  }));

  return (
    <div data-testid="salary-page">
      <PageHeader
        eyebrow="Module 02"
        title="Salary Intelligence"
        subtitle={data.market_label ? `Market: ${data.market_label}` : `AI market benchmark in ${currency}`}
        actions={
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="salary-refresh" className="gap-2">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing" : "Refresh"}
          </Button>
        }
      />

      {(converted || data.is_converted_estimate) && (
        <div className="mb-6 flex items-start gap-2 rounded-lg border border-[hsl(var(--warning))/0.4] bg-[hsl(var(--warning))/0.08] text-[hsl(var(--warning))] px-4 py-3 text-sm" data-testid="salary-converted-notice">
          <Info size={14} className="shrink-0 mt-0.5" />
          <div>
            <strong>{converted ? "Currency-converted view." : "Approximate figures."}</strong>{" "}
            {converted
              ? `AI returned values in ${from}; we've converted them to ${currency} using today's cached rate for display.`
              : `Localized labor-market data wasn't available for your country; AI approximated using regional benchmarks.`}
            {" "}Add your country in Settings for accurate local benchmarks.
          </div>
        </div>
      )}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="salary-metrics">
        <Metric label="Current" value={<Money value={data.current_salary} from={from} />} sub="Your reported" />
        <Metric
          label="Market median"
          value={<Money value={data.market_median} from={from} />}
          sub={data.underpaid_by > 0 ? <>Under <Money value={data.underpaid_by} from={from} /></> : "At/above"}
          accent={data.underpaid_by > 0}
        />
        <Metric label="Top 10%" value={<Money value={data.market_top_10} from={from} />} sub="Aspirational" />
        <Metric
          label="Lifetime earnings"
          value={<Money value={data.lifetime_earnings_projection} from={from} compact />}
          sub="20-yr projection"
        />
      </section>

      {data.commentary && (
        <div className="bg-card border border-border rounded-xl p-6 mb-8 border-l-4 border-l-primary" data-testid="salary-commentary">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">AI Analysis</div>
          <div className="text-base text-foreground leading-relaxed">{data.commentary}</div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-xl p-6" data-testid="salary-comparison-chart">
          <div className="font-display font-bold text-lg tracking-tight mb-4">Salary comparison</div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} />
              <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} tickFormatter={v => format(v, { compact: true, decimals: 0 })} />
              <Tooltip formatter={v => [format(v), "Salary"]} />
              <Bar dataKey="salary" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-xl p-6" data-testid="salary-progression-chart">
          <div className="font-display font-bold text-lg tracking-tight mb-4">Salary by years of experience</div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={progression}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="years" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} label={{ value: 'Years', position: 'insideBottom', offset: -5, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} tickFormatter={v => format(v, { compact: true, decimals: 0 })} />
              <Tooltip formatter={v => [format(v), "Salary"]} />
              <Line type="monotone" dataKey="salary" stroke="hsl(var(--success))" strokeWidth={2} dot={{ fill: "hsl(var(--success))" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, sub, accent }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">{label}</div>
      <div className="font-display font-black text-2xl md:text-3xl tracking-tight">{value}</div>
      <div className={`text-xs mt-2 font-mono ${accent ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`}>{sub}</div>
    </div>
  );
}
